import type { QueryClient } from "@tanstack/react-query";

export type MediaJobLiveStatus =
  | "pending"
  | "processing"
  | "encoding"
  | "transcribing"
  | "refining"
  | "done"
  | "failed";

export interface PartsProgress {
  done: number;
  total: number;
}

export interface MediaJobStreamState {
  /** True once the realtime connection is open. */
  isConnected: boolean;
  /** Most-recent semantic status derived from the event stream. */
  status: MediaJobLiveStatus;
  /** Defaults to {0,0} until an encode_completed or part_progress event arrives. */
  partsProgress: PartsProgress;
  /** Last error message set by `media.processing.run.failed`. */
  lastError?: string;
  /** Always false post-migration; retained for API compatibility. */
  terminated: boolean;
  /** Always false post-migration; retained for API compatibility. */
  useFallbackPolling: boolean;
  /** Word count from the refine_completed event. */
  wordCount?: number;
  /** Running cost in USD captured from part_progress and refine_completed events. */
  costUsd?: number;
}

export const INITIAL_MEDIA_LIVE_STATE: MediaJobStreamState = {
  isConnected: false,
  status: "pending",
  partsProgress: { done: 0, total: 0 },
  terminated: false,
  useFallbackPolling: false,
};

export const mediaLiveKeys = {
  run: (runId: string) => ["media.run.live-state", runId] as const,
};

export function seedMediaLiveState(qc: QueryClient, runId: string) {
  const key = mediaLiveKeys.run(runId);
  if (!qc.getQueryData(key)) {
    qc.setQueryData<MediaJobStreamState>(key, INITIAL_MEDIA_LIVE_STATE);
  }
}
